import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Grid,
  FormLabel,
  Typography,
  TextField,
} from "@mui/material";
import { styled } from "@mui/system";

import {
  getAllMasterTables,
  getMetadataByTableName,
  getRowsByTableName,
  createRowForTable,
  deleteRowBycolumnInMaster,
  UpdateTableData,
} from "../../services/MasterTablesService/MasterTableService";

import DeleteConfirmationModal from "../../Modals/DeleteConfirmationModal";
import AddMasterTableDataModal from "../../Modals/AddMasterTableDataModal";
import DynamicTable from "./DynamicTable";
import { snakeCasetoTitleCase } from "../../helpers/utils";
import moment from "moment";
import CustomDatePicker from "../DatePicker/DatePicker";
import { useParams } from "react-router-dom";
import { type } from "@testing-library/user-event/dist/type";
import SnackBars from "../snackbars/SnackBars";

const FormGrid = styled(Grid)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px", // Added gap for spacing between label and input
}));

const MasterTable = () => {
  const [tables, setTables] = useState([]);
  const { tab } = useParams();
  const [selectedTable, setSelectedTable] = useState(() => {
    return tab || ""; // Set default to the 'table' query param or an empty string
  });

  const [selectedTableDisplayName, setSelectedTableDisplayName] = useState();
  const [columns, setColumns] = useState([]);
  const [headings, setHeadings] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [newData, setNewData] = useState({});
  const [metadata, setMetadata] = useState([]);
  const [errors, setErrors] = useState({});
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] = useState(null);
  const [typeDelete, setTypeDelete] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);
  const [totalSitesCount, setTotalSitesCount] = useState(0);
  const [updatedData, setUpdatedData] = useState({});
  const [selectedDate, setSelectedDate] = useState({
    valid_from: "",
    valid_to: "",
    validTo: "",
    validFrom: "",
  });
  const tablePrimaryKeys = {
    ActivityMasterMetadata: "ID",
    PrincipleDetailsMaster: "ProjectID",
    CompanyMaster: "company_id",
    StageMaster: "StageID",
    StakeHolderMaster: "PersonID",
    LocationMaster: "LocationID",
    StructureDesignMaster: "StructureDesignID",
    organization_master: "id",
    domain_master: "id",
    user_role_mapping: "id",
    principle_city_package_mapping: "id",
    role_master: "id",
    partner_master: "id",
    Package: "PackageID",
    status_master: "id",
    remark_category_master: "id",
    attachment_category_master: "id",
    site_premiumness_master: "id",
    site_process_master: "id",
    corporation_city_mapping: "id",
    site_commertials_master: "id",
  };
  const [updatingData, setUpdatingData] = useState(false);
  useEffect(() => {
    getTables();
  }, []);
  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
    if (selectedTable) {
      getMetadata(selectedTable);
    }
  }, [selectedTable]);

  useEffect(() => {
    fetchTableData(selectedTable);
  }, [pagination.pageIndex, pagination.pageSize, selectedTable]);

  useEffect(() => {
    const selectedTableObj = tables.find((elem) => elem.name === selectedTable);
    setSelectedTableDisplayName(selectedTableObj?.display_name || "");
  }, [selectedTable, tables]);

  const getTables = async () => {
    try {
      const masterTables = await getAllMasterTables();
      setTables(masterTables);
    } catch (error) {
      console.log("error in get Tables", error);
    }
  };

  const fetchTableData = async (table) => {
    try {
      setLoading(true);
      setSelectedTable(table);
      console.log("");

      if (table === "") return;
      const selectedData = await getRowsByTableName(
        table,
        pagination.pageIndex + 1,
        pagination.pageSize
      );

      setTotalSitesCount(selectedData.totalData);
      setTableData(selectedData.result);
      if (selectedData.result.length > 0) {
        constructColumnArray(selectedData.result[0], table);
      }
      setLoading(false);
    } catch (error) {
      console.log("error in fetchTableData", error.message);
    } finally {
      setLoading(false);
    }
  };

  function constructColumnArray(data, table) {
    const headings = Object.keys(data).map((heading) => heading);

    setHeadings(headings);

    const tableheaders = headings.filter(
      (col) => col !== tablePrimaryKeys[table]
    );

    const colobj = tableheaders.map((col) => ({
      // accessorKey: col,
      accessorFn: (data) => {
        if (
          col === "valid_from" ||
          col === "valid_to" ||
          col === "validFrom" ||
          col === "validTo"
        )
          return moment(data[col]);
        else {
          return data[col]?.toString();
        }
      },
      id: col,
      header: snakeCasetoTitleCase(col),
      size: 12,
      enableEditing: col === "valid_to" || col === "validTo",
      filterVariant:
        col === "valid_to" ||
        col === "valid_from" ||
        col === "validFrom" ||
        col === "validTo"
          ? "date-range"
          : "text",
      Cell:
        col === "valid_to" ||
        col === "valid_from" ||
        col === "validFrom" ||
        col === "validTo"
          ? ({ cell }) => cell.getValue().format("MM/DD/YYYY")
          : undefined,
      Edit: ({ cell, row }) => {
        const handleValueChange = (newValue) => {
          setUpdatedData((prev) => ({
            ...prev,
            [col]: newValue,
          }));
        };

        return col === "valid_from" ||
          col === "valid_to" ||
          col === "validFrom" ||
          col === "validTo" ? (
          <CustomDatePicker
            cell={cell}
            row={row}
            col={col}
            handleValueChange={handleValueChange}
            errors={errors}
          />
        ) : (
          <TextField
            value={cell.getValue()}
            label={snakeCasetoTitleCase(col)}
            disabled
          />
        );
      },
    }));
    console.log("Column ", colobj);

    setColumns(colobj);
  }

  const getMetadata = async (table) => {
    try {
      setLoading(true);
      const meta = await getMetadataByTableName(table);
      console.log("meta", meta);
      setMetadata(meta);
      setLoading(false);
    } catch (error) {
      console.log("error in get Tables", error);
    } finally {
      setLoading(false);
    }
  };

  const getPrimaryKey = () => tablePrimaryKeys[selectedTable] || null;

  const validateData = () => {
    let valid = true;
    const newErrors = {};

    metadata.forEach((column) => {
      const value = newData[column.column_name];

      // Date validation for required and valid date check
      if (column.data_type === "timestamp without time zone") {
        if (!value) {
          newErrors[column.column_name] = `${column.column_name} is required`;
          valid = false;
        } else {
          const currentDate = moment().startOf("day");

          if (
            column.column_name === "valid_to" ||
            column.column_name === "validTo"
          ) {
            const validFromDate =
              column.column_name === "valid_to"
                ? newData.valid_from
                : newData.validFrom;

            if (moment(value).isBefore(currentDate)) {
              newErrors[
                column.column_name
              ] = `${column.column_name} must be a future date`;
              valid = false;
            }

            if (
              validFromDate &&
              moment(value).isBefore(moment(validFromDate))
            ) {
              newErrors[
                column.column_name
              ] = `${column.column_name} must be after valid_from date`;
              valid = false;
            }
          }
        }
      }

      // Required field validation for non-date fields
      if (
        column.is_nullable === "NO" &&
        column.data_type !== "timestamp without time zone" &&
        (!value || value.trim() === "")
      ) {
        newErrors[column.column_name] = `${column.column_name} is required`;
        valid = false;
      }

      // Character length validation
      if (
        column.character_maximum_length &&
        value &&
        value.length > column.character_maximum_length
      ) {
        newErrors[
          column.column_name
        ] = `${column.column_name} exceeds max length of ${column.character_maximum_length}`;
        valid = false;
      }

      // Integer validation
      if (column.data_type === "integer" && value && isNaN(value)) {
        newErrors[
          column.column_name
        ] = `${column.column_name} must be a number`;
        valid = false;
      }
    });

    setErrors(newErrors);
    return valid;
  };

  const addData = async () => {
    try {
      if (validateData()) {
        await createRowForTable(selectedTable, newData);
        fetchTableData(selectedTable);
        setOpenAddDialog(false);

        setOpenSnackBar(true);
        setSnackBarMessage({
          title: "Data Added Successfully",
          type: "success",
        });
      }
    } catch (error) {
      console.log("error in add data", error);
      setOpenSnackBar(true);
      setSnackBarMessage({
        title: "Some error occured",
        type: " ",
      });
    }
  };

  async function handleEditData({ row, values, table }) {
    setUpdatingData(true);
    const primaryKey = row.original[getPrimaryKey()];

    if (updatedData) {
      const data = await UpdateTableData(
        updatedData,
        selectedTable,
        primaryKey
      );

      if (data) {
        fetchTableData(selectedTable);
      }
    }
    setUpdatingData(false);
    table.setEditingRow(null);

    setOpenSnackBar(true);
    setSnackBarMessage({
      title: "Data Edited Successfully",
      type: "success",
    });
  }

  const deleteRow = async () => {
    const column = getPrimaryKey();
    console.log("column------>", column, selectedTable, selectedRowToDelete);

    try {
      await deleteRowBycolumnInMaster(
        selectedTable,
        column,
        selectedRowToDelete
      );
      console.log("Table length ", tableData.length);

      if (tableData.length == 1) {
        setPagination((prev) => {
          return {
            ...prev,
            pageIndex: prev.pageIndex >= 1 ? prev.pageIndex - 1 : -1,
          };
        });
      }

      fetchTableData(selectedTable);

      setTypeDelete("");
      setOpenDeleteDialog(false);

      setOpenSnackBar(true);
      setSnackBarMessage({
        title: "Data Deleted Successfully",
        type: "success",
      });
    } catch (err) {
      console.log("error in delete row", err);
    }
  };

  const handleDeleteClick = (obj) => {
    const rowId = obj[getPrimaryKey()];
    setSelectedRowToDelete(rowId);
    setOpenDeleteDialog(true);
  };

  // Open the dialog form
  const handleClickOpen = () => {
    setNewData({}); // Reset new data
    setErrors({}); // Clear errors
    setOpenAddDialog(true); // Open the dialog
  };

  // Close the dialog form
  const handleClose = () => {
    setOpenAddDialog(false);
    setSelectedDate({
      valid_from: "",
      valid_to: "",
      validTo: "",
      validFrom: "",
    });
  };
  const handleCloseDeleteDialog = () => {
    setTypeDelete("");
    setOpenDeleteDialog(false);
  };

  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState({
    title: "",
    type: "",
  });

  const handleCloseSnackBar = () => {
    setOpenSnackBar(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <SnackBars
        open={openSnackBar}
        setOpen={setOpenSnackBar}
        handleClose={handleCloseSnackBar}
        title={snackBarMessage.title}
        type={snackBarMessage.type}
      />
      <h1>Master Management</h1>
      <Grid container display="flex" justifyContent="space-between">
        {/* <FormControl sx={{ width: "40%" }} size="small">
          <InputLabel>Select a table</InputLabel>
          <Select
            value={selectedTable}
            onChange={(e) => fetchTableData(e.target.value)}
            label="Select a table"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {tables.map((table) => (
              <MenuItem key={table} value={table}>
                {table}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}

        <FormGrid xs={12} md={4}>
          <FormLabel htmlFor="city">
            Select a master table to get required data
          </FormLabel>

          <Select
            value={selectedTable}
            onChange={(e) => fetchTableData(e.target.value)}
            size="small"
            variant="outlined"
            required
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {tables.map((table) => (
              <MenuItem key={table.name} value={table.name}>
                {table.display_name}
              </MenuItem>
            ))}
          </Select>
        </FormGrid>
      </Grid>

      {selectedTable && (
        <>
          <h2>{selectedTableDisplayName}</h2>
          {tableData.length > 0 ? (
            <DynamicTable
              data={tableData}
              columns={columns}
              isEnablePagination={true}
              totalSitesCount={totalSitesCount}
              setPagination={setPagination}
              pagination={pagination}
              enableEditing={true}
              isSearchBarEnable={true}
              isFilterEnable={true}
              headings={headings}
              handleDeleteClick={handleDeleteClick}
              handleEditData={handleEditData}
              updatingData={updatingData}
              isHeaderBackgroundColor={false}
              showTableTopToolbar={true}
              enableColumnActions={true}
              isEnableSorting={true}
            />
          ) : (
            <Typography>
              No data have been added yet. Please click here to add.
            </Typography>
          )}

          <Button
            variant="contained"
            sx={{
              margin: "16px 0",
              backgroundColor: "#b41660",
              color: "#fff",
            }}
            onClick={handleClickOpen}
          >
            Add New Data
          </Button>

          {/* Add Data Dialog */}
          <AddMasterTableDataModal
            selectedTableDisplayName={selectedTableDisplayName}
            loading={loading}
            setLoading={setLoading}
            open={openAddDialog}
            onClose={handleClose}
            onAddData={addData}
            metadata={metadata}
            newData={newData}
            setNewData={setNewData}
            errors={errors}
            setErrors={setErrors}
            selectedTable={selectedTable}
            validateData={validateData}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />

          {/* Confirm Delete Dialog */}
          <DeleteConfirmationModal
            typeDelete={typeDelete}
            setTypeDelete={setTypeDelete}
            open={openDeleteDialog}
            onClose={handleCloseDeleteDialog}
            onConfirm={deleteRow}
          />
        </>
      )}
    </Box>
  );
};

export default MasterTable;
